import type { Project } from "../types/project";

interface WebGLFrameProps {
  project: Project;
}

export function WebGLFrame({ project }: WebGLFrameProps) {
  const webgl = project.webgl;

  if (!webgl) return null;

  return (
    <section className="border-b border-zinc-800 py-10 last:border-b-0">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-[9px] tracking-[0.22em] text-zinc-600">
            INTERACTIVE BUILD
          </p>
          <h2 className="mt-2 text-2xl font-light uppercase tracking-[-0.02em] text-zinc-100">
            Playable demo
          </h2>
          {webgl.note ? (
            <p className="mt-2 max-w-xl text-[13px] font-light leading-6 text-zinc-500">
              {webgl.note}
            </p>
          ) : null}
        </div>

        {webgl.enabled ? (
          <a
            href={webgl.src}
            target="_blank"
            rel="noreferrer"
            className="border border-zinc-700 px-3 py-2 font-mono text-[9px] tracking-[0.16em] text-zinc-400 transition-colors hover:border-zinc-100 hover:bg-zinc-100 hover:text-black focus-visible:border-zinc-100 focus-visible:bg-zinc-100 focus-visible:text-black focus-visible:outline-none"
          >
            OPEN STANDALONE ↗
          </a>
        ) : null}
      </div>

      {webgl.enabled ? (
        <div
          className="relative w-full overflow-hidden border border-zinc-700 bg-black"
          style={{ aspectRatio: webgl.aspectRatio ?? "16 / 9" }}
        >
          <iframe
            src={webgl.src}
            title={`${project.title} WebGL build`}
            className="absolute inset-0 h-full w-full border-0"
            allow="autoplay; fullscreen; gamepad; clipboard-read; clipboard-write"
            allowFullScreen
            loading="lazy"
          />
        </div>
      ) : (
        <div
          className="webgl-placeholder relative grid min-h-72 place-items-center overflow-hidden border border-dashed border-zinc-700 bg-black p-6 text-center"
          style={{ aspectRatio: webgl.aspectRatio ?? "16 / 9" }}
        >
          <div className="relative z-10 max-w-xl">
            <p className="font-mono text-[9px] tracking-[0.24em] text-zinc-600">
              BUILD // NOT YET PUBLIC
            </p>
            <p className="mt-5 text-sm font-light leading-7 text-zinc-400">
              The playable demo is still in development. When the browser build
              is ready it will run here, inside this page — no download, no
              launcher.
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
